'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard, Newspaper, Zap, FileText, ScrollText,
  Twitter, Coins, HelpCircle, Wrench, Hexagon,
  Network, Inbox, ClipboardCheck, Megaphone, Send, Activity,
  Clock, Image as ImageIcon, ScrollText as LogIcon, Users as UsersIcon, Trash2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { usePreviewAutoGrant } from '../../hooks/usePreviewAutoGrant';
import { Permission } from '../../types/permissions';
import { UserRole } from '../../lib/constants';
import { NeedsYouBadge, useOpsBadges } from './ops/NavBadges';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavSection {
  title: string;
  permission: Permission;
  items: NavItem[];
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ReactNode;
  permission: Permission;
  /** When set, render a count pill sourced from the ONE nav-badge source (useOpsBadges). */
  badgeKey?: 'needsYou' | 'monitor' | 'graphIntake' | 'coverageRequests';
}

interface SidebarContentProps {
  sections: NavSection[];
  isCurrentPage: (href: string) => boolean;
  userData: any;
  handleSignOut: () => void;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { userData, signOut, hasMinimumRole } = useAuth();
  const { hasPermission } = usePermissions();
  const pathname = usePathname();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Identity-based WS-K preview: a signed-in admin/owner auto-mints the preview cookies for THIS
  // browser (desktop or phone) — no WSK_PREVIEW_KEY env var, no secret /preview?key= URL. Fires once
  // when the cookie is absent; the existing WS-K nav-link reveal is the visual confirmation.
  usePreviewAutoGrant(hasMinimumRole(UserRole.ADMIN));

  // Operations-shell navigation — 6 workflow groups (agent-oversight surface).
  // Entire shell gated to admin/owner; Users is owner-only.
  const ICON = "w-5 h-5";
  const navigationSections: NavSection[] = [
    { title: 'The Desk', permission: Permission.ADMIN_MENU_ACCESS, items: [
      { name: 'Today', href: '/admin/today', icon: <LayoutDashboard className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Review', href: '/admin/review', icon: <ClipboardCheck className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Stories', href: '/admin/stories', icon: <Newspaper className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Breaking News', href: '/admin/breaking-news', icon: <Zap className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Articles', href: '/admin/articles', icon: <FileText className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Ecosystem Recaps', href: '/admin/ecosystem-recaps', icon: <ScrollText className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
    ]},
    { title: 'Sources', permission: Permission.ADMIN_MENU_ACCESS, items: [
      { name: 'Twitter', href: '/admin/twitter', icon: <Twitter className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Tracked Assets', href: '/admin/assets', icon: <Coins className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
    ]},
    { title: 'Oversight', permission: Permission.ADMIN_MENU_ACCESS, items: [
      // Agent Activity + Monitor are ABSORBED into Activity (spec §4/§9): agent-activity → ?actor=agent,
      // monitor → ?subject=health (with a link to the full under-capture report). Both routes still
      // resolve — agent-activity redirects; monitor is kept for its rich per-signal detail. The monitor
      // under-capture badge rides on Activity now so the attention signal isn't lost from the nav.
      { name: 'Activity', href: '/admin/activity', icon: <Activity className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS, badgeKey: 'monitor' },
      { name: 'Open Questions', href: '/admin/open-questions', icon: <HelpCircle className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Self-Heal', href: '/admin/self-heal', icon: <Wrench className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Gold Ledger', href: '/admin/gold-ledger', icon: <Hexagon className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
    ]},
    { title: 'Knowledge Graph', permission: Permission.ADMIN_MENU_ACCESS, items: [
      // coverageRequests: F4 red-link demand — pending reader "Request coverage" clicks land as
      // a count pill here (the editor's entity surface), no new admin page.
      { name: 'Entities', href: '/admin/tags', icon: <Network className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS, badgeKey: 'coverageRequests' },
      { name: 'Graph Intake', href: '/admin/graph-intake', icon: <Inbox className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS, badgeKey: 'graphIntake' },
    ]},
    { title: 'Distribution', permission: Permission.ADMIN_MENU_ACCESS, items: [
      { name: 'Posted to Twitter', href: '/admin/posted-to-twitter', icon: <Send className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Announcements', href: '/admin/announcements', icon: <Megaphone className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
    ]},
    { title: 'System', permission: Permission.ADMIN_MENU_ACCESS, items: [
      { name: 'Scheduled Jobs', href: '/admin/scheduled-jobs', icon: <Clock className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Media Library', href: '/admin/media-library', icon: <ImageIcon className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Activity Log', href: '/admin/activity-log', icon: <LogIcon className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
      { name: 'Users', href: '/admin/users', icon: <UsersIcon className={ICON} />, permission: Permission.OWNER_MENU_ACCESS },
      { name: 'Trash', href: '/admin/trash', icon: <Trash2 className={ICON} />, permission: Permission.ADMIN_MENU_ACCESS },
    ]},
  ];

  // Filter sections and items based on user permissions
  // Filter sections to show if user has any visible items OR has section permission
  // This allows per-user grants to surface items without needing full section access
  const filteredSections = navigationSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => hasPermission(item.permission))
    }))
    .filter(section => section.items.length > 0);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      // Handle sign out error silently
    }
  };

  const isCurrentPage = (href: string) => {
    return pathname === href;
  };

  return (
    <div className="h-screen flex bg-[#0b0d12]">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-50 md:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-[#12151c] shadow-xl">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <SidebarContent sections={filteredSections} isCurrentPage={isCurrentPage} userData={userData} handleSignOut={handleSignOut} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent sections={filteredSections} isCurrentPage={isCurrentPage} userData={userData} handleSignOut={handleSignOut} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden bg-[#0b0d12]">
        {/* Mobile header */}
        <div className="md:hidden bg-[#12151c] border-b border-[#242a35] px-4 py-3 flex items-center">
          <button
            type="button"
            className="mr-3 h-10 w-10 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-[#1a1f29] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#E8A832] transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center space-x-3">
            <div
              aria-label="Descout"
              className="h-8 w-8 rounded-lg flex items-center justify-center text-[#0b0d12] text-sm font-bold"
              style={{ background: 'linear-gradient(135deg, #E8A832 0%, #10b981 100%)' }}
            >
              D
            </div>
            <div>
              <p className="text-lg font-semibold text-white">Descout</p>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          {children}
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ sections, isCurrentPage, userData, handleSignOut }: SidebarContentProps) {
  // The ONE nav-badge source — per-item counts all originate here (zeros until the
  // engine writes; NeedsYouBadge renders null at 0, so this is byte-neutral now).
  const navBadges = useOpsBadges();
  return (
    <div className="flex flex-col h-full bg-[#12151c] border-r border-[#242a35]">
      {/* Logo/Brand */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        <div className="flex items-center flex-shrink-0 px-6 py-6 border-b border-[#242a35]">
          <div
            aria-label="Descout"
            className="h-10 w-10 rounded-xl shadow-sm flex items-center justify-center text-[#0b0d12] text-base font-bold"
            style={{ background: 'linear-gradient(135deg, #E8A832 0%, #10b981 100%)' }}
          >
            D
          </div>
          <div className="ml-3">
            <p className="text-xl font-bold text-white">Descout</p>
            <p className="text-[11px] text-gray-500 font-semibold uppercase tracking-wider">Operations</p>
          </div>
        </div>

        {/* Navigation with Sections */}
        <nav className="flex-1 px-4 py-6 space-y-6">
          {sections.map((section, sectionIndex) => (
            <div key={section.title}>
              {/* Section Header */}
              <div className="px-3 mb-2">
                <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  {section.title}
                </h3>
              </div>

              {/* Section Items */}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const current = isCurrentPage(item.href);
                  return (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${current
                        ? 'bg-[rgba(232,168,50,0.12)] text-[#E8A832] border-l-2 border-[#E8A832]'
                        : 'text-gray-400 hover:bg-[#1a1f29] hover:text-white'
                        }`}
                    >
                      <span className={`mr-3 flex-shrink-0 transition-colors ${current ? 'text-[#E8A832]' : 'text-gray-500 group-hover:text-gray-300'}`}>
                        {item.icon}
                      </span>
                      <span className="truncate">{item.name}</span>
                      {item.badgeKey ? (
                        <span className="ml-auto pl-2">
                          <NeedsYouBadge count={navBadges[item.badgeKey]} />
                        </span>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* User section */}
      <div className="flex-shrink-0 border-t border-[#242a35] p-4">
        <div className="flex items-center w-full">
          <Link
            href="/admin/profile"
            className="flex items-center flex-1 min-w-0 rounded-lg -m-1 p-1 hover:bg-[#1a1f29] transition-colors"
          >
            <div className="flex-shrink-0">
              {userData?.profileImage ? (
                <img
                  src={userData.profileImage}
                  alt={userData.displayName || 'User'}
                  className="h-9 w-9 rounded-full object-cover ring-2 ring-[#242a35] shadow-sm"
                />
              ) : (
                <div className="h-9 w-9 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center shadow-sm">
                  <span className="text-sm font-semibold text-white">
                    {userData?.displayName?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
              )}
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{userData?.displayName}</p>
              <p className="text-xs text-gray-500 flex items-center mt-0.5">
                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${userData?.role === 'owner' ? 'bg-amber-500' :
                  userData?.role === 'admin' ? 'bg-purple-400' :
                    userData?.role === 'editor' ? 'bg-green-400' : 'bg-blue-400'
                  }`} />
                <span className="capitalize font-medium">{userData?.role}</span>
              </p>
            </div>
          </Link>
          <div className="ml-3">
            <button
              onClick={handleSignOut}
              className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
              title="Sign out"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
